/**
 * Persist an already verified, isolated claims sign-in without replacing other
 * accounts. No token acquisition, token decoding, account selection, or logging.
 * Uses only MSAL's public serialization/ICachePlugin transaction contract.
 */
import { platform } from "node:process";
import { TokenCacheContext, type ICachePlugin, type TokenCache } from "@azure/msal-node";
import {
  validateClaimsBinding,
  type PowerPlatformClaimsBinding,
} from "./claims-challenge-store.js";
import { createPersistentCachePlugin } from "./token-cache.js";

const MAX_BYTES = 4 * 1024 * 1024;
const SECTIONS = ["Account", "IdToken", "AccessToken", "RefreshToken", "AppMetadata"] as const;
type Section = (typeof SECTIONS)[number];
type ObjectRecord = Record<string, unknown>;
type Snapshot = Record<Section, ObjectRecord> & ObjectRecord;
const EMPTY = JSON.stringify(Object.fromEntries(SECTIONS.map((section) => [section, {}])));
const FIELDS: Record<Section, readonly string[]> = {
  Account: [
    "home_account_id",
    "environment",
    "realm",
    "local_account_id",
    "username",
    "authority_type",
    "name",
    "client_info",
    "last_modification_time",
    "last_modification_app",
    "tenantProfiles",
  ],
  IdToken: ["home_account_id", "environment", "credential_type", "client_id", "secret", "realm"],
  AccessToken: [
    "home_account_id",
    "environment",
    "credential_type",
    "client_id",
    "secret",
    "realm",
    "target",
    "cached_at",
    "expires_on",
    "extended_expires_on",
    "refresh_on",
    "token_type",
  ],
  RefreshToken: [
    "home_account_id",
    "environment",
    "credential_type",
    "client_id",
    "secret",
    "family_id",
    "target",
    "realm",
  ],
  AppMetadata: ["client_id", "environment", "family_id"],
};

function invalid(): never {
  throw new Error(
    "Claims sign-in cache does not match the verified account, application, and Power Platform resource; nothing was persisted.",
  );
}

function object(value: unknown): ObjectRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) invalid();
  return value as ObjectRecord;
}

function parseSnapshot(serialized: string): Snapshot {
  if (typeof serialized !== "string" || Buffer.byteLength(serialized, "utf8") > MAX_BYTES)
    invalid();
  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    invalid();
  }
  // Reject dangerous property names before merging, including in unknown shared
  // sections. Bound recursive validation independently of the JSON byte limit.
  let nodes = 0;
  function check(value: unknown, depth: number): void {
    if (++nodes > 100_000 || depth > 32) invalid();
    if (value !== null && typeof value === "object") {
      for (const [key, child] of Object.entries(value)) {
        if (["__proto__", "prototype", "constructor"].includes(key)) invalid();
        check(child, depth + 1);
      }
    }
  }
  check(parsed, 0);
  const snapshot = object(parsed);
  for (const section of SECTIONS) {
    if (snapshot[section] === undefined) snapshot[section] = {};
    object(snapshot[section]);
  }
  return snapshot as Snapshot;
}

function text(value: unknown, max = 4096): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= max &&
    !/[\p{Cc}\p{Cf}]/u.test(value)
  );
}

function matchesIdentity(
  row: ObjectRecord,
  section: Section,
  binding: PowerPlatformClaimsBinding,
): void {
  const account = binding.account;
  if (row.environment !== account.environment) invalid();
  if (section !== "AppMetadata" && row.home_account_id !== account.homeAccountId) invalid();
  if (section === "Account") {
    if (row.local_account_id !== account.localAccountId || row.realm !== account.tenantId)
      invalid();
  } else if (row.client_id !== binding.clientId) invalid();
  if ((section === "IdToken" || section === "AccessToken") && row.realm !== account.tenantId)
    invalid();
  if (section !== "Account" && section !== "AppMetadata" && row.credential_type !== section)
    invalid();
}

function validateIsolated(snapshot: Snapshot, binding: PowerPlatformClaimsBinding): void {
  if (Object.keys(snapshot).some((key) => !SECTIONS.includes(key as Section))) invalid();
  if (Object.keys(snapshot.Account).length !== 1 || Object.keys(snapshot.AccessToken).length !== 1)
    invalid();
  for (const section of SECTIONS) {
    if (Object.keys(snapshot[section]).length > 1) invalid();
    for (const [key, value] of Object.entries(snapshot[section])) {
      const row = object(value);
      if (Object.keys(row).some((field) => !FIELDS[section].includes(field))) invalid();
      matchesIdentity(row, section, binding);
      for (const [field, item] of Object.entries(row)) {
        if (field === "tenantProfiles") continue;
        // Empty optional scope/realm/family values occur in MSAL serialization.
        if (
          typeof item !== "string" ||
          item.length > (field === "secret" ? 1024 * 1024 : 4096) ||
          /[\p{Cc}\p{Cf}]/u.test(item)
        )
          invalid();
      }
      let expectedKey: string;
      if (section === "Account") {
        if (row.authority_type !== "MSSTS" || !text(row.username)) invalid();
        if (row.tenantProfiles !== undefined) {
          if (!Array.isArray(row.tenantProfiles) || row.tenantProfiles.length > 1) invalid();
          for (const profile of row.tenantProfiles) {
            if (!text(profile)) invalid();
            let decoded: unknown;
            try {
              decoded = JSON.parse(profile);
            } catch {
              invalid();
            }
            const tenantProfile = object(decoded);
            if (
              Object.keys(tenantProfile).some(
                (field) =>
                  !["tenantId", "localAccountId", "name", "username", "isHomeTenant"].includes(
                    field,
                  ),
              ) ||
              tenantProfile.tenantId !== binding.account.tenantId ||
              tenantProfile.localAccountId !== binding.account.localAccountId
            )
              invalid();
            if (tenantProfile.name !== undefined && !text(tenantProfile.name)) invalid();
            if (tenantProfile.username !== undefined && !text(tenantProfile.username)) invalid();
            if (
              tenantProfile.isHomeTenant !== undefined &&
              typeof tenantProfile.isHomeTenant !== "boolean"
            )
              invalid();
          }
        }
        expectedKey = [
          binding.account.homeAccountId,
          binding.account.environment,
          binding.account.homeAccountId.split(".")[1] || binding.account.tenantId,
        ].join("-");
      } else if (section === "AppMetadata") {
        if (row.family_id) invalid(); // This is an app-owned registration, not a family client.
        expectedKey = `appmetadata-${binding.account.environment}-${binding.clientId}`;
      } else {
        if (!text(row.secret, 1024 * 1024) || /\s/u.test(row.secret)) invalid();
        if (section === "AccessToken") {
          if (row.token_type !== "Bearer" || !text(row.target)) invalid();
          const scopes = row.target.split(" ");
          const resourcePrefix = `${binding.resource}/`;
          if (
            !scopes.some((scope) => scope.startsWith(resourcePrefix)) ||
            scopes.some(
              (scope) =>
                !["openid", "profile", "offline_access"].includes(scope) &&
                (!scope.startsWith(resourcePrefix) ||
                  !/^[A-Za-z0-9._]+$/.test(scope.slice(resourcePrefix.length))),
            )
          )
            invalid();
          for (const field of ["cached_at", "expires_on", "extended_expires_on", "refresh_on"]) {
            if (
              (field === "cached_at" || field === "expires_on" || row[field] !== undefined) &&
              (typeof row[field] !== "string" || !/^\d{1,12}$/.test(row[field]))
            )
              invalid();
          }
        }
        if (section === "RefreshToken" && (row.family_id || row.target || row.realm)) invalid();
        expectedKey = [
          binding.account.homeAccountId,
          binding.account.environment,
          section,
          binding.clientId,
          typeof row.realm === "string" ? row.realm : "",
          typeof row.target === "string" ? row.target : "",
          "",
        ].join("-");
      }
      // Key formulas are the installed SDK's serialized cache contract, not
      // private field access. Reject unsupported future key formats safely.
      if (key !== expectedKey.toLowerCase()) invalid();
    }
  }
}

async function abort(plugin: ICachePlugin, context: TokenCacheContext): Promise<void> {
  const abortable = plugin as ICachePlugin & {
    abortCacheAccess?: (context: TokenCacheContext) => Promise<void>;
  };
  if (abortable.abortCacheAccess) await abortable.abortCacheAccess(context);
  else await plugin.afterCacheAccess(new TokenCacheContext(context.tokenCache, false));
}

/** Account keys span tenants. Replace only the verified tenant's profile. */
function mergeProfiles(existing: unknown, incoming: unknown): unknown {
  if (existing === undefined) return incoming;
  if (incoming === undefined) return existing;
  if (!Array.isArray(existing) || existing.length > 200 || !Array.isArray(incoming)) invalid();
  const updates = new Map<string, ObjectRecord>();
  for (const serialized of incoming) {
    const profile = object(JSON.parse(serialized as string));
    updates.set(profile.tenantId as string, profile);
  }
  const seen = new Set<string>();
  const merged = existing.map((serialized: unknown) => {
    if (!text(serialized, 16_384)) invalid();
    const profile = object(JSON.parse(serialized));
    if (
      !text(profile.tenantId, 512) ||
      !text(profile.localAccountId, 512) ||
      seen.has(profile.tenantId)
    )
      invalid();
    seen.add(profile.tenantId);
    const update = updates.get(profile.tenantId);
    if (!update) return serialized;
    if (update.localAccountId !== profile.localAccountId) invalid();
    updates.delete(profile.tenantId);
    return JSON.stringify({ ...profile, ...update });
  });
  return [...merged, ...Array.from(updates.values(), (profile) => JSON.stringify(profile))];
}

/**
 * Caller MUST first verify the isolated device-code result token and exact
 * identity. This helper additionally validates every outgoing SDK cache record.
 * Linux is refused: the current factory can fall back to an unlocked writer
 * that suppresses I/O errors, which cannot provide this transaction guarantee.
 * Complexity: O(snapshot bytes), bounded to 4 MiB per input/output snapshot.
 */
export async function persistVerifiedClaimsCache(
  isolatedTokenCache: TokenCache,
  binding: PowerPlatformClaimsBinding,
  isStillCurrent: () => boolean,
): Promise<void> {
  if (platform !== "darwin" && platform !== "win32") {
    throw new Error(
      "Verified claims sign-in persistence requires locked macOS or Windows secure storage; the Linux fallback is not supported. No claims tokens were persisted.",
    );
  }
  let plugin: ICachePlugin | undefined;
  let context: TokenCacheContext | undefined;
  let opened = false;
  try {
    if (
      !validateClaimsBinding(binding) ||
      isolatedTokenCache.persistence ||
      isStillCurrent() !== true
    )
      invalid();
    const expected: PowerPlatformClaimsBinding = {
      clientId: binding.clientId,
      resource: binding.resource,
      account: { ...binding.account },
    };
    // Snapshot before any persistence read. Never let loading the shared cache
    // mutate the isolated PCA or silently replace the verified credentials.
    const verified = parseSnapshot(isolatedTokenCache.serialize());
    validateIsolated(verified, expected);
    let serialized = EMPTY;
    const holder = {
      serialize: () => serialized,
      deserialize: (value: string) => {
        parseSnapshot(value); // Enforce bounds before outer plugin guards inspect it.
        serialized = value;
      },
    };
    context = new TokenCacheContext(holder, true);
    plugin = await createPersistentCachePlugin();
    if (isStillCurrent() !== true) invalid();
    opened = true;
    await plugin.beforeCacheAccess(context);
    const shared = parseSnapshot(holder.serialize());
    for (const section of SECTIONS) {
      for (const [key, value] of Object.entries(verified[section])) {
        const incoming = object(value);
        const existing = shared[section][key];
        if (existing !== undefined) matchesIdentity(object(existing), section, expected);
        const mergedRecord = { ...(existing === undefined ? {} : object(existing)), ...incoming };
        if (section === "Account" && existing !== undefined) {
          const profiles = mergeProfiles(object(existing).tenantProfiles, incoming.tenantProfiles);
          if (profiles !== undefined) mergedRecord.tenantProfiles = profiles;
        }
        shared[section][key] = mergedRecord;
      }
    }
    const merged = JSON.stringify(shared);
    if (Buffer.byteLength(merged, "utf8") > MAX_BYTES) invalid();
    holder.deserialize(merged);
    if (isStillCurrent() !== true) invalid();
    await plugin.afterCacheAccess(context);
    opened = false;
  } catch {
    if (opened && plugin && context) {
      try {
        await abort(plugin, context);
      } catch {
        throw new Error(
          "Claims cache persistence failed and secure-storage cleanup could not be confirmed. No automatic retry was attempted.",
        );
      }
    }
    // Never attach MSAL/persistence errors or cache contents as a cause.
    throw new Error(
      "Verified claims cache could not be persisted safely. The selected account was not changed; no automatic retry was attempted.",
    );
  }
}
